/** Optimistisch angezeigte eigene Nachricht (senden/hochladen/fehlgeschlagen). */
import { FileText, RotateCcw, Trash2, X } from 'lucide-react';
import { memo } from 'react';
import { Avatar } from '../../components/Avatar';
import { useAppStore } from '../../store/app';
import type { PendingMessage } from '../../store/types';
import { formatBytes, formatShortTime } from './format';
import { Markdown } from './Markdown';
import { discardPending, pendingFiles, retryPending } from './send';
import { useMessagesUi } from './state';
import { useAuthor } from './MessageItem';

function PendingItemImpl({ pending: p, grouped, serverId, panel }: { pending: PendingMessage; grouped: boolean; serverId: string | null; panel?: boolean }) {
  const me = useAppStore((s) => s.me);
  const member = useAppStore((s) => (serverId && me ? s.servers[serverId]?.me : undefined));
  const author = useAuthor({ author: me, member: member ?? null, webhookId: null, overrideName: null, overrideAvatarUrl: null }, serverId);
  const upload = useMessagesUi((s) => s.uploads[p.nonce]);
  const files = pendingFiles(p.nonce);
  const pct = upload && upload.total ? Math.min(100, Math.round((upload.loaded / upload.total) * 100)) : 0;
  return (
    <li className={`msg msg-pending ${grouped ? 'msg-grouped' : ''} ${panel ? 'msg-panel' : ''}`} data-testid="pending-message" data-status={p.status} data-nonce={p.nonce} aria-busy={p.status === 'sending'}>
      <div className="msg-row">
        <div className="msg-gutter">
          {grouped ? (
            <time className="msg-time">{formatShortTime(p.createdAt)}</time>
          ) : (
            <span className="msg-avatar">
              <Avatar user={author.user} overrideUrl={author.avatarUrl} size={panel ? 32 : 40} />
            </span>
          )}
        </div>
        <div className="msg-main">
          {!grouped && (
            <header className="msg-header">
              <span className="msg-author" style={author.color ? { color: author.color } : undefined}>
                {author.name}
              </span>
              <time className="msg-time">{p.status === 'sending' ? 'wird gesendet …' : formatShortTime(p.createdAt)}</time>
            </header>
          )}
          {p.content && (
            <div className="msg-content" data-testid="message-content">
              <Markdown content={p.content} serverId={serverId} />
            </div>
          )}
          {files.length > 0 && (
            <div className="pending-files">
              {files.map((f) => (
                <div key={f.id} className="attachment-file">
                  <FileText size={24} aria-hidden="true" />
                  <div className="attachment-file-text">
                    <span className="truncate">{f.file.name}</span>
                    <span className="faint small">{formatBytes(f.file.size)}</span>
                  </div>
                </div>
              ))}
              {upload && (
                <div className="upload-progress" data-testid="upload-progress">
                  <progress className="progress" max={100} value={pct} aria-label={`Hochladen ${pct} %`} />
                  <span className="small faint">
                    {pct} % · {formatBytes(upload.loaded)} von {formatBytes(upload.total)}
                  </span>
                  <button type="button" className="icon-btn" onClick={() => upload.abort?.()} aria-label="Hochladen abbrechen" data-testid="upload-cancel">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
          {p.status === 'failed' && (
            <div className="pending-error" role="alert" data-testid="pending-error">
              <span>{p.error ?? 'Senden fehlgeschlagen.'}</span>
              <button type="button" className="btn btn-sm" onClick={() => void retryPending(p.channelId, p.nonce)} data-testid="pending-retry">
                <RotateCcw size={14} /> Erneut senden
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => discardPending(p.channelId, p.nonce)} data-testid="pending-discard">
                <Trash2 size={14} /> Verwerfen
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export const PendingItem = memo(PendingItemImpl);
